import React, { useState, useEffect } from "react";
import {
  Modal,
  Box,
  Button,
  Typography,
  Grid,
  TextField,
  MenuItem,
  FormControl,
  Select,
  InputLabel,
} from "@mui/material";
import { LocalizationProvider, StaticDatePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import addDays from "date-fns/addDays";
import isBefore from "date-fns/isBefore";
import set from "date-fns/set";
import { useNavigate } from "react-router-dom";

const ConsultationBookingModal = ({ open, onClose, onSubmit }) => {
  const [step, setStep] = useState(1); // 1: Appointment details, 2: Patient info
  const [consultationType, setConsultationType] = useState("");
  const [date, setDate] = useState(null);
  const [time, setTime] = useState(null);
  const [appointments, setAppointments] = useState(null);
  const [status, setStatus] = useState("idle");
  // Patient information state for step 2:
  const [patientInfo, setPatientInfo] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    dateOfBirth: "",
  });
  const navigate = useNavigate();

  const fetchAppointments = async () => {
    const currentDate = new Date().toISOString().split("T")[0];
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/appointments/${currentDate}`
      );
      const data = await response.json();
      const formattedData = formatAppointments(data);
      const indexedAppointments = indexAppointmentsByDate(formattedData);
      setAppointments(indexedAppointments);
    } catch (error) {
      console.error("Error fetching appointments:", error);
    }
  };

  const formatAppointments = (appointments) => {
    return appointments.map((appointment) => {
      const formattedDate = new Date(appointment.date)
        .toISOString()
        .split("T")[0];
      const formattedTime = appointment.start_time.slice(0, 5);
      return {
        ...appointment,
        date: formattedDate,
        start_time: formattedTime,
      };
    });
  };

  const indexAppointmentsByDate = (appointments) => {
    const indexedAppointments = {};
    appointments.forEach((appointment) => {
      const { date, start_time } = appointment;
      const [hour, minute] = start_time.split(":");
      const hourInt = parseInt(hour, 10);
      const ampm = hourInt >= 12 ? "PM" : "AM";
      const formattedHour = hourInt % 12 === 0 ? 12 : hourInt % 12;
      const formattedTime = `${formattedHour}:${minute} ${ampm}`;
      if (!indexedAppointments[date]) {
        indexedAppointments[date] = [];
      }
      indexedAppointments[date].push(formattedTime);
    });
    return indexedAppointments;
  };

  useEffect(() => {
    fetchAppointments();
  }, [open]);

  // Pharmacy operating hours by day
  const hours = {
    Monday: { start: "10:50", end: "17:50" },
    Tuesday: { start: "10:50", end: "18:10" },
    Wednesday: { start: "10:50", end: "18:10" },
    Thursday: { start: "10:50", end: "18:10" },
    Friday: { start: "10:50", end: "17:50" },
    Saturday: { start: "10:50", end: "13:50" },
    Sunday: null,
  };

  // Generate time slots in 20-minute increments
  const generateTimeSlots = (date) => {
    const dayOfWeek = date
      ? date.toLocaleDateString("en-US", { weekday: "long" })
      : "";
    const dayHours = hours[dayOfWeek];
    if (!dayHours) return [];
    const dateKey = date ? date.toISOString().split("T")[0] : date;
    const slots = [];
    let current = set(new Date(), {
      hours: parseInt(dayHours.start.split(":")[0], 10),
      minutes: parseInt(dayHours.start.split(":")[1], 10),
      seconds: 0,
      milliseconds: 0,
    });
    const end = set(new Date(), {
      hours: parseInt(dayHours.end.split(":")[0], 10),
      minutes: parseInt(dayHours.end.split(":")[1], 10),
    });
    while (isBefore(current, end)) {
      let timeSlot =
        current.toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        })[0] !== "0"
          ? current.toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            })
          : current
              .toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
              .substring(1, 8);
      if (
        appointments &&
        appointments[dateKey] &&
        appointments[dateKey].includes(timeSlot)
      ) {
        // Skip slot if already booked
      } else {
        slots.push(timeSlot);
      }
      current = new Date(current.getTime() + 20 * 60000);
    }
    return slots;
  };

  const shouldDisableDate = (date) => {
    const today = new Date();
    const maxDate = addDays(today, 14);
    return isBefore(date, today) || isBefore(maxDate, date);
  };

  const timeSlots = generateTimeSlots(date);

  // Step 1: Validate consultation fields and proceed to next step
  const handleNext = () => {
    if (date && time && consultationType) {
      setStep(2);
    } else {
      alert("Please select consultation reason, date, and time.");
    }
  };

  // Step 2: Final submission with patient info
  const handleFinalSubmit = async () => {
    if (
      !patientInfo.firstName ||
      !patientInfo.lastName ||
      !patientInfo.email ||
      !patientInfo.phone ||
      !patientInfo.dateOfBirth
    ) {
      alert("Please fill in all patient information fields.");
      return;
    }
    try {
      const response = await onSubmit({
        consultationType,
        date,
        time,
        patientInfo,
      });
      if (response && response.status && response.status === 201) {
        setStatus("success");
      } else {
        setStatus("error");
      }
    } catch (error) {
      setStatus("error");
    }
  };

  // Handler for input changes in patient info form
  const handlePatientInfoChange = (e) => {
    setPatientInfo({ ...patientInfo, [e.target.name]: e.target.value });
  };

  // Reset state when modal closes
  const handleClose = () => {
    setStep(1);
    setStatus("idle");
    setConsultationType("");
    setDate(null);
    setTime(null);
    setPatientInfo({
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      dateOfBirth: "",
    });
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "90%",
          maxWidth: 800,
          bgcolor: "background.paper",
          boxShadow: 24,
          p: 4,
          borderRadius: 2,
          overflowY: "auto",
          maxHeight: "650px",
        }}
      >
        {status === "idle" && step === 1 && (
          <>
            <Typography variant="h6" mb={2}>
              Book Appointment - Step 1
            </Typography>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Select Consultation Reason</InputLabel>
              <Select
                value={consultationType}
                onChange={(e) => setConsultationType(e.target.value)}
                label="Select Consultation Reason"
              >
                <MenuItem value="Minor Ailment">Minor Ailment</MenuItem>
                <MenuItem value="Medication Check">Medication Check</MenuItem>
              </Select>
            </FormControl>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Box className="grid grid-cols-1 sm:grid-cols-2 gap-2 ">
                <StaticDatePicker
                  displayStaticWrapperAs="desktop"
                  value={date}
                  onChange={(newValue) => {
                    setDate(newValue);
                    setTime(null);
                  }}
                  shouldDisableDate={shouldDisableDate}
                  renderInput={(params) => <TextField {...params} />}
                  minDate={new Date()}
                  maxDate={addDays(new Date(), 14)}
                />
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="subtitle1" mb={1}>
                    Select Time Slot
                  </Typography>
                  <Grid container spacing={1}>
                    {timeSlots.length > 0 ? (
                      timeSlots.map((slot, index) => (
                        <Grid item xs={4} sm={3} key={index}>
                          <Button
                            variant={time === slot ? "contained" : "outlined"}
                            color="primary"
                            fullWidth
                            sx={{ fontSize: "13.7px" }}
                            onClick={() => setTime(slot)}
                          >
                            {slot}
                          </Button>
                        </Grid>
                      ))
                    ) : (
                      <Typography variant="body2" color="textSecondary">
                        No available slots for this day.
                      </Typography>
                    )}
                  </Grid>
                </Box>
              </Box>
            </LocalizationProvider>
            <Box
              sx={{ display: "flex", justifyContent: "space-between", mt: 3 }}
            >
              <Button onClick={handleClose} variant="outlined" color="error">
                Close
              </Button>
              <Button onClick={handleNext} variant="contained" color="primary">
                Next
              </Button>
            </Box>
          </>
        )}
        {status === "idle" && step === 2 && (
          <>
            <Typography variant="h6" mb={2}>
              Book Appointment - Step 2: Patient Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="First Name"
                  name="firstName"
                  value={patientInfo.firstName}
                  onChange={handlePatientInfoChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Last Name"
                  name="lastName"
                  value={patientInfo.lastName}
                  onChange={handlePatientInfoChange}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  value={patientInfo.email}
                  onChange={handlePatientInfoChange}
                  type="email"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  name="phone"
                  value={patientInfo.phone}
                  onChange={handlePatientInfoChange}
                  type="tel"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Date of Birth"
                  name="dateOfBirth"
                  value={patientInfo.dateOfBirth}
                  onChange={handlePatientInfoChange}
                  type="date"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                mt: 3,
              }}
            >
              <Button onClick={() => setStep(1)} variant="outlined">
                Back
              </Button>
              <Button
                onClick={handleFinalSubmit}
                variant="contained"
                color="primary"
              >
                Submit
              </Button>
            </Box>
          </>
        )}
        {status === "success" && (
          <Box textAlign="center">
            <Typography variant="h6" color="primary">
              Appointment Booked Successfully!
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                navigate("/");
                handleClose();
              }}
              sx={{ mt: 2 }}
            >
              Go to home
            </Button>
          </Box>
        )}
        {status === "error" && (
          <Box textAlign="center">
            <Typography variant="h6" color="error">
              Booking Failed
            </Typography>
            <Typography variant="body2" mt={1}>
              Sorry, this appointment might already be taken. Please try again
              or call the pharmacy if the issue persists.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={() => setStatus("idle")}
              sx={{ mt: 2 }}
            >
              Try Again
            </Button>
          </Box>
        )}
      </Box>
    </Modal>
  );
};

export default ConsultationBookingModal;
